import * as React from 'react'
import { Grid, Box, Card, CardContent, Stack, Typography, Slider, FormControl, InputAdornment, OutlinedInput, InputLabel, Select, MenuItem, CircularProgress, Link, Dialog, DialogContent, DialogActions, DialogTitle, Button, Tooltip, TextField, Chip, IconButton, Collapse } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { PieChart } from '@mui/x-charts/PieChart';
import { LineChart } from '@mui/x-charts/LineChart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { useLocation } from 'react-router-dom';

import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useTranslation } from 'react-i18next';

import MainLayout from '../components/layout/Main';
import SessionStressHeatmap from '../components/SessionStressHeatmap';
import api from "../api"

function valuetext(value) {
  return `${value} min`;
}

function secondsToTimeString(d) {
  let seconds = d % 60
  d = Math.floor(d / 60)
  let minutes = d % 60
  let hours = Math.floor(d / 60)
  
  // Pad minutes and seconds with leading zeros
  const paddedMinutes = minutes < 10 ? `0${minutes}` : minutes
  const paddedSeconds = seconds < 10 ? `0${seconds}` : seconds
  
  return `${hours}:${paddedMinutes}:${paddedSeconds}`
}

function DashboardDetails() {
  const location = useLocation();
  const [selectedRecord, setSelectedRecord] = React.useState([])
  const [devices, setDevices] = React.useState([])
  const [records, setRecords] = React.useState([])
  const [filteredRecords, setFilteredRecords] = React.useState([])
  const [selectedGrouping, setSelectedGrouping] = React.useState("average")

  const [chartData, setChartData] = React.useState({z: 0, i: 0, n: 0})
  const [totalTime, setTotalTime] = React.useState([0, 100])
  const [maxTime, setMaxTime] = React.useState(100)  
  const [lineChartData, setLineChartData] = React.useState([[], [], []])
  const [dialogOpen, setDialogOpen] = React.useState(false)

  const [session, setSession] = React.useState('');
  const [selectedDevice, setSelectedDevice] = React.useState('');

  const [loading, setLoading] = React.useState(false)
  
  // Filter states
  const [showFilters, setShowFilters] = React.useState(false)
  const [dateFrom, setDateFrom] = React.useState(null)
  const [dateTo, setDateTo] = React.useState(null)
  const [searchText, setSearchText] = React.useState('')
  const [minDuration, setMinDuration] = React.useState('')
  const [maxDuration, setMaxDuration] = React.useState('')
  const [activeFilters, setActiveFilters] = React.useState(0)

  const { t, i18n } = useTranslation()
  const theme = useTheme()

  const marks = [
    {
      value: 0,
      label: '0 min',
    },
    {
      value: maxTime,
      label: `${maxTime} min`,
    },
  ];

  let data = [
    { label: t("dashboard_details.balanced_text"), value: chartData.z, color: '#4caf50'},
    { label: t("dashboard_details.intensive_text"), value: chartData.i, color: '#fbc02d'},
    { label: t("dashboard_details.dangerous_text"), value: chartData.n, color: '#f44336'},
  ]

  let grouping_options = [
    { label: "dashboard_details.average_label", value: "average" },
    { label: "dashboard_details.max_label", value: "max" },
  ]

  React.useEffect(() => {
    setLoading(true)
    getDevices()
    document.title = t("dashboard_details.page_title")
  }, [])

  React.useEffect(() => {
    if(totalTime[1] != maxTime || totalTime[0] != 0) {
      let endTime = totalTime[1] * 60
      if (totalTime[0] != 0 && totalTime[1] == maxTime) {
        endTime = totalTime[1] * 60 - 1
      } 
      let z_sum = 0, i_sum = 0, n_sum = 0
      for (let i = (totalTime[0] * 60);i <= endTime;i++) {
        let tab = selectedRecord.pie_data[i]
        if (tab == 1) z_sum += 1
        else if (tab == 2) i_sum += 1
        else if (tab == 3) n_sum += 1
        
      }
      let lineChartTemp = []
      for (let i = totalTime[0];i <= totalTime[1];i++) {
        lineChartTemp.push(selectedRecord.data[i])
      }
      setChartData({z: z_sum, i: i_sum, n: n_sum})
      setLineChartData(lineChartTemp)
    }
  }, [totalTime])

  React.useEffect(() => {
    getRecords(session, selectedGrouping)
  }, [session])

  React.useEffect(() => {
    try {
      setSession(records[0].id)
    } catch (err) {

    }
  }, [records])

  // Apply filters whenever records or filter values change
  React.useEffect(() => {
    let filtered = [...records];
    let filterCount = 0;

    // Search filter
    if (searchText) {
      filtered = filtered.filter(record => {
        const dateStr = new Date(record.created_at).toLocaleDateString(i18n.resolvedLanguage, { 
          weekday:"long", year:"numeric", month:"short", day:"numeric", hour: "2-digit", minute: "2-digit"
        }).toLowerCase();
        return dateStr.includes(searchText.toLowerCase());
      });
      filterCount++;
    }

    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(record => 
        dayjs(record.created_at).isAfter(dayjs(dateFrom).startOf('day')) || 
        dayjs(record.created_at).isSame(dayjs(dateFrom), 'day')
      );
      filterCount++;
    }

    if (dateTo) {
      filtered = filtered.filter(record => 
        dayjs(record.created_at).isBefore(dayjs(dateTo).endOf('day')) || 
        dayjs(record.created_at).isSame(dayjs(dateTo), 'day')
      );
      filterCount++;
    }

    // Session duration filter (in minutes)
    if (minDuration !== '' && !isNaN(minDuration)) {
      const minSeconds = parseFloat(minDuration) * 60;
      filtered = filtered.filter(record => {
        const durationSeconds = record.duration || 0;
        return durationSeconds >= minSeconds;
      });
      filterCount++;
    }

    if (maxDuration !== '' && !isNaN(maxDuration)) {
      const maxSeconds = parseFloat(maxDuration) * 60;
      filtered = filtered.filter(record => {
        const durationSeconds = record.duration || 0;
        return durationSeconds <= maxSeconds;
      });
      filterCount++;
    }

    setFilteredRecords(filtered);
    setActiveFilters(filterCount);
    
    // Reset session selection if current session is not in filtered results
    if (session && !filtered.find(r => r.id === session)) {
      setSession('');
    }
  }, [records, searchText, dateFrom, dateTo, minDuration, maxDuration, i18n.resolvedLanguage, session])

  React.useEffect(() => {
    try {
      setLoading(true)
      let z_sum = 0, i_sum = 0, n_sum = 0
      selectedRecord.pie_data.forEach((tab) => {
        if (tab == 1) z_sum += 1
        else if (tab == 2) i_sum += 1
        else if (tab == 3) n_sum += 1
      })
      setChartData({z: z_sum, i: i_sum, n: n_sum})
      setMaxTime(Math.round(selectedRecord.pie_data.length / 60))
      setTotalTime([0, Math.round(selectedRecord.pie_data.length / 60)])
    } catch (err) {
      console.log(err)
    }
  }, [selectedRecord])

  React.useEffect(() => {
    try {
      // Check if a device was passed from navigation state (from Dashboard)
      if (location.state?.selectedDevice) {
        setSelectedDevice(location.state.selectedDevice)
      } else if (devices.length > 0) {
        setSelectedDevice(devices[0].id)
      }
    } catch (err) {

    }
  }, [devices, location.state])

  React.useEffect(() => {
    getRecordList(selectedDevice)
  }, [selectedDevice])

  React.useEffect(() => {
    getLineChartData(session, selectedGrouping)
  }, [selectedGrouping])

  React.useEffect(() => {
    if(chartData.z != 0 || chartData.i != 0 || chartData.n != 0) setLoading(false)
  }, [chartData])

  const getRecords = (id, grouping="average") => {
    if (id != '') {
      api
        .get(`/api/record/${id}/`, { params: { "grouping": grouping }})
        .then((res) => res.data)
        .then((data) => {
          setLineChartData(data.data)
          setSelectedRecord(data)
          console.log(data)
        })
        .catch((err) => console.log(err))
    }
  }

  const getLineChartData = (id,  grouping="average") => {
    if (id != '') {
      api
        .get(`/api/record/line-chart/${id}/`, { params: { "grouping": grouping }})
        .then((res) => res.data)
        .then((data) => {
          setLineChartData(data.data)
        })
        .catch((err) => console.log(err))
    }
  }

  const getDevices = () => {
    api
      .get("/api/devices/")
      .then((res) => res.data)
      .then((data) => {
        setDevices(data)
      })
      .catch((err) => console.log(err))
  }

  const getRecordList = (id) => {
    if (id != '') {
      api
        .get(`/api/record/list/${id}/`)
        .then((res) => res.data)
        .then((data) => {
          setRecords(data)
        })
        .catch((err) => console.log(err))
    }
  }

  const handleSessionChange = (event) => {
    setSession(event.target.value);
  };

  const handleDeviceChange = (event) => {
    setSelectedDevice(event.target.value);
    // Clear current session when device changes - will be set to new device's first record
    setSession('');
    setSelectedRecord([]);
  };

  const handleSliderChange = (event, newValue) => {
    setTotalTime(newValue);
  };

  const handleInputTimeChangeDown = (event) => {
    if (event.target.value < totalTime[1] && event.target.value >= 0)
    setTotalTime([event.target.value, totalTime[1]])
  }

  const handleInputTimeChangeUp = (event) => {
    if (event.target.value > totalTime[0] && event.target.value <= maxTime)
    setTotalTime([totalTime[0], event.target.value])
  }

  const handleGroupingChange = (event) => {
    setSelectedGrouping(event.target.value)
    setTotalTime([0, Math.round(selectedRecord.pie_data.length / 60)])
    console.log(selectedRecord)
  }

  const handleDialogClose = (event) => {
    setDialogOpen(false)
  }

  const handleClearFilters = () => {
    setSearchText('');
    setDateFrom(null);
    setDateTo(null);
    setMinDuration('');
    setMaxDuration('');
  }

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  }

  function percentOfTotalData(value) {
    let p = (value / (chartData.z + chartData.n + chartData.i)) * 100
    let percent = Math.round(p * 100) / 100
    if ((percent * 100) % 10 == 0 && (percent * 100) % 100 != 0) return `${percent}0%`
    return `${percent}%`
  }
  

  return (
    <MainLayout backToDashboard={true}>
      <CircularProgress sx={{ display: loading ? '' : 'none'}}/>
        <Grid container spacing={3} sx={{ display: loading ? 'none' : ''}}>
          <Grid item lg={8} xs={12}>
            <Card 
              variant='outlined' 
              sx={{ 
                borderRadius: '16px',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              }}
            >
              {/* <CardHeader title='Użytkowanie' /> */}
              <CardContent>
                <Typography 
                  variant='h5' 
                  sx={{ 
                    marginBottom: 3, 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    borderLeft: '4px solid #dc3545',
                    paddingLeft: 2,
                  }}
                >
                  {t("dashboard_details.chart_title")}
                </Typography>
                <Stack spacing={2} direction="row">
                  <PieChart 
                    series={[
                      {
                        paddingAngle: 2,
                        cornerRadius: 5,
                        innerRadius: 60,
                        outerRadius: 140,
                        data,
                        highlighted: { 
                          additionalRadius: 10 
                        },
                        highlightScope: {
                          highlight: 'item',
                          // fade: 'global',
                        }
                      },
                    ]}
                    margin={{ right: 5 }}
                    width={300}
                    height={300}
                    legend={{ hidden: true }}
                  />
                  <Stack direction="column" spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', paddingRight: "5vw" }}>
                    {data.map((item) => {
                      const label = item.label;
                      // const Icon = iconMapping[label];

                      return (
                        <Stack key={label} spacing={1} sx={{ alignItems: 'center' }}>
                          {/* {Icon ? <Icon fontSize="var(--icon-fontSize-lg)" /> : null} */}
                          <Typography sx={{ color: item.color }} variant="h6">{label}</Typography>
                          <Tooltip title={t("dashboard_details.time_format_tooltip") || "Time format: hours:minutes:seconds"} arrow>
                            <Typography variant="h6" sx={{ cursor: 'help' }}>
                              {percentOfTotalData(item.value)} ({secondsToTimeString(item.value)})
                            </Typography>
                          </Tooltip>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Stack>
                <Box sx={{ marginTop: 2, marginX: 4 }}>
                  <Slider
                    value={totalTime}
                    getAriaLabel={() => 'Time slider'}
                    onChange={handleSliderChange}
                    getAriaValueText={valuetext}
                    valueLabelDisplay="auto"
                    max={maxTime}
                    marks={marks}
                  />
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <FormControl variant="outlined">
                      <OutlinedInput
                        type='number'
                        placeholder="Od"
                        id="minuteStartInput"
                        endAdornment={<InputAdornment position="end">min</InputAdornment>}
                        value={totalTime[0]}
                        onChange={handleInputTimeChangeDown}
                        
                      />
                    </FormControl>
                    <ArrowForwardIcon fontSize='large'color='primary'/>
                    <FormControl variant="outlined">
                      <OutlinedInput
                        type='number'
                        placeholder="Do"
                        id="minuteEndInput"
                        endAdornment={<InputAdornment position="end">min</InputAdornment>}
                        value={totalTime[1]}
                        onChange={handleInputTimeChangeUp}
                      />
                    </FormControl>
                  </Stack>
                </Box>
                <LineChart 
                  yAxis={[
                    {
                      min: -5,
                      max: 100,
                      label: '[%]',
                      colorMap: {
                        type: 'piecewise',
                        thresholds: [5, 52.75],
                        colors: ['#4caf50', '#ffeb3b', '#f44336'],
                      }
                    }
                  ]}
                  series={[
                    {
                      data: lineChartData,
                      showMark: false,
                      // area: true,
                    }
                  ]}
                  xAxis={[
                    {
                      label: '[min]',
                      data: lineChartData.map((item, index) => (index)),
                    }
                  ]}
                  height={300}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item lg={4} xs={12}>
            <Card 
              variant='outlined' 
              sx={{ 
                borderRadius: '16px', 
                marginBottom: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              }}
            >
              <CardContent>
                <Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      color: theme.palette.text.primary,
                      mb: 2 
                    }}
                  >
                    {t("dashboard_details.choose_device_text")}
                  </Typography>
                  <FormControl sx={{ marginBottom: 2 }} fullWidth size="small">
                    <InputLabel id="deivce-select-label">{t("dashboard_details.choose_device_label")}</InputLabel>
                    <Select
                      labelId="device-select-label"
                      id="device"
                      value={selectedDevice}
                      label={t("dashboard_details.choose_device_label")}
                      onChange={handleDeviceChange}
                    >
                      {devices.map((item) => <MenuItem value={item.id} key={item.id}>{item.name || `${t("dashboard_details.vehicle")} ${item.id}`}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2, mb: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t("dashboard_details.choose_session_text")}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {activeFilters > 0 && (
                        <Chip 
                          label={`${activeFilters} ${activeFilters > 1 ? t('dashboard_details.filters_active_plural') : t('dashboard_details.filters_active')}`} 
                          size="small" 
                          color="primary"
                          onDelete={handleClearFilters}
                          deleteIcon={<ClearIcon />}
                        />
                      )}
                      <IconButton 
                        size="small" 
                        onClick={handleToggleFilters}
                        sx={{
                          bgcolor: showFilters ? theme.palette.action.selected : 'transparent'
                        }}
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  
                  <Collapse in={showFilters}>
                    <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: theme.palette.background.default }}>
                      <Stack spacing={2.5}>
                        {/* Search Filter */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                            {t('dashboard_details.filter_search_label')}
                          </Typography>
                          <TextField
                            size="small"
                            label={t('dashboard_details.filter_search_placeholder')}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder={t('dashboard_details.filter_search_type_placeholder')}
                            fullWidth
                          />
                        </Box>

                        {/* Date Range Filter */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                            {t('dashboard_details.filter_date_range_label')}
                          </Typography>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack direction="row" spacing={1}>
                              <DatePicker
                                label={t('dashboard_details.filter_date_from')}
                                value={dateFrom}
                                onChange={(newValue) => setDateFrom(newValue)}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                              />
                              <DatePicker
                                label={t('dashboard_details.filter_date_to')}
                                value={dateTo}
                                onChange={(newValue) => setDateTo(newValue)}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                              />
                            </Stack>
                          </LocalizationProvider>
                        </Box>

                        {/* Session Duration Filter */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                            {t('dashboard_details.filter_duration_label')}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                              size="small"
                              label={t('dashboard_details.filter_duration_min')}
                              type="number"
                              value={minDuration}
                              onChange={(e) => setMinDuration(e.target.value)}
                              placeholder="0"
                              inputProps={{ min: 0, step: 1 }}
                              fullWidth
                            />
                            <Typography variant="body2" color="text.secondary">-</Typography>
                            <TextField
                              size="small"
                              label={t('dashboard_details.filter_duration_max')}
                              type="number"
                              value={maxDuration}
                              onChange={(e) => setMaxDuration(e.target.value)}
                              placeholder="∞"
                              inputProps={{ min: 0, step: 1 }}
                              fullWidth
                            />
                          </Stack>
                        </Box>
                      </Stack>
                    </Card>
                  </Collapse>

                  <FormControl sx={{ marginY: 2 }} fullWidth size="small">
                    {/* <InputLabel id="session-select-label">{t("dashboard_details.choose_session_label")}</InputLabel> */}
                    <Select
                      labelId="session-select-label"
                      id="session"
                      value={session}
                      // label={t("dashboard_details.choose_session_label")}
                      onChange={handleSessionChange}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 400,
                          },
                        },
                      }}
                    >
                      {filteredRecords.length === 0 ? (
                        <MenuItem disabled>
                          <Typography variant="body2" color="text.secondary">
                            {t('dashboard_details.no_sessions_found')}
                          </Typography>
                        </MenuItem>
                      ) : (
                        filteredRecords.map((item) => (
                          <MenuItem value={item.id} key={item.id}>
                            <Stack direction="row" spacing={1} alignItems="center" width="100%">
                              <Typography variant="body2">
                                {t("dashboard_details.record_from_text")} {new Date(item.created_at).toLocaleDateString(i18n.resolvedLanguage, { weekday:"long", year:"numeric", month:"short", day:"numeric", hour: "2-digit", minute: "2-digit", second: '2-digit'})}
                              </Typography>
                            </Stack>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  {filteredRecords.length > 0 && filteredRecords.length < records.length && (
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard_details.showing_sessions', { filtered: filteredRecords.length, total: records.length })}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
            <Card 
              variant='outlined' 
              sx={{ 
                borderRadius: '16px',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              }}
            >
              <CardContent>
                <Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      color: theme.palette.text.primary,
                      mb: 2 
                    }}
                  >
                    {t("dashboard_details.choose_data_grouping_method")} <Link component="button" onClick={() => setDialogOpen(true)}>(?)</Link>
                  </Typography>
                  <Dialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                  >
                    <DialogTitle>
                      {t("dashboard_details.data_grouping_question")}
                    </DialogTitle>
                    <DialogContent>
                      <Typography>
                        <span style={{ fontWeight: "bold", fontSize: "120%" }} >{t("dashboard_details.average_label")} - </span>{t("dashboard_details.average_description")}
                      </Typography>
                      <Typography>
                        <span style={{ fontWeight: "bold", fontSize: "120%" }} >{t("dashboard_details.max_label")} - </span>{t("dashboard_details.max_description")}
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button autoFocus variant='contained' onClick={handleDialogClose}>
                        ok
                      </Button>
                    </DialogActions>
                  </Dialog>
                  <FormControl sx={{ marginTop: 2 }} fullWidth size="small">
                    <InputLabel id="grouping-select-label">{t("dashboard_details.data_grouping_label")}</InputLabel>
                    <Select
                      labelId="grouping-select-label"
                      id="grouping"
                      value={selectedGrouping}
                      label={t("dashboard_details.data_grouping_label")}
                      onChange={handleGroupingChange}
                    >
                      {grouping_options.map((item) => <MenuItem value={item.value}>{t(item.label)}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Session Stress Heatmap */}
          {selectedRecord && selectedRecord.pie_data && (
            <Grid item xs={12}>
              <SessionStressHeatmap 
                pieData={selectedRecord.pie_data}
                startTime={selectedRecord.creadet_at}
              />
            </Grid>
          )}
        </Grid>
    </MainLayout> 
  );
}

export default DashboardDetails;